import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, Phone, User, Clock, MessageSquare, ExternalLink } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contacts`);
      const data = await response.json();
      
      if (data.success) {
        setContacts(data.contacts);
      } else {
        setError('Mesajlar yüklənərkən xəta baş verdi');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Mesajlar yüklənərkən xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('az-AZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      new: { label: 'Yeni', color: 'bg-blue-100 text-blue-700' },
      read: { label: 'Oxunub', color: 'bg-gray-100 text-gray-700' },
      replied: { label: 'Cavablandırılıb', color: 'bg-green-100 text-green-700' }
    };
    const statusInfo = statusMap[status] || statusMap.new;
    return (
      <Badge className={`${statusInfo.color} hover:${statusInfo.color}`}>
        {statusInfo.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Mesajlar yüklənir...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchContacts} className="bg-red-600 hover:bg-red-700">
              Yenidən cəhd edin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">Əlaqə Mesajları</h1>
          <p className="text-red-100">
            Cəmi {contacts.length} mesaj
          </p>
        </div>
      </div>

      {/* Messages List */}
      <div className="container mx-auto px-4 py-8">
        {contacts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Hələ mesaj yoxdur
              </h3>
              <p className="text-gray-600">
                Əlaqə formasından göndərilən mesajlar burada görünəcək
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {contacts.map((contact) => (
              <Card key={contact._id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{contact.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          {formatDate(contact.created_at)}
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(contact.status)}
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Mövzu: {contact.subject}
                    </h4>
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {contact.message}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-4 pt-4 border-t">
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="font-medium">{contact.email}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    <a
                      href={`tel:${contact.phone}`}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span className="font-medium">{contact.phone}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => window.open(`mailto:${contact.email}?subject=Cavab: ${contact.subject}`, '_blank')}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email ilə cavabla
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`tel:${contact.phone}`)}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Zəng et
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <Button
            onClick={fetchContacts}
            variant="outline"
            className="border-red-600 text-red-600 hover:bg-red-50"
          >
            Yenilə
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminContacts;
